/**
 * D351: one economy for every theme. The shared economy file holds every number, keyed by
 * position; a theme file holds only what is its own (ids, names, words, currency, map terrain),
 * in the same shape and the same order. `mergeTheme` lays one over the other:
 *
 * - objects merge key by key; a leaf set on both sides is an error, so a theme cannot carry a
 *   number the economy already has, nor the economy a name;
 * - arrays on both sides merge entry by entry and must be the same length, so a theme that
 *   leaves a position unnamed (or names one too many) fails to load;
 * - then every reference the economy writes as `@<path>/<n>` (`@producers/3`, `@map.lands/1`)
 *   becomes the id at that position of the merged content.
 *
 * Plain JavaScript, so `scripts/content-tables.mjs` can merge without a TypeScript runner.
 */

const REF = /^@([A-Za-z.]+)\/(\d+)$/;

function isObject(v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function overlay(eco, theme, path, errors) {
  if (eco === undefined) return theme;
  if (theme === undefined) return eco;
  if (Array.isArray(eco) && Array.isArray(theme)) {
    if (eco.length !== theme.length)
      errors.push(
        `${path}: the economy has ${eco.length} positions, the theme names ${theme.length}`,
      );
    return eco.map((e, i) => overlay(e, theme[i], `${path}[${i + 1}]`, errors));
  }
  if (isObject(eco) && isObject(theme)) {
    const out = {};
    for (const k of new Set([...Object.keys(eco), ...Object.keys(theme)]))
      out[k] = overlay(eco[k], theme[k], path ? `${path}.${k}` : k, errors);
    return out;
  }
  errors.push(`${path}: set by both the economy and the theme`);
  return eco;
}

function resolve(value, root, path, errors) {
  if (typeof value === 'string') {
    const m = REF.exec(value);
    if (!m) return value;
    const list = m[1].split('.').reduce((a, k) => (isObject(a) ? a[k] : undefined), root);
    const entry = Array.isArray(list) ? list[Number(m[2]) - 1] : undefined;
    const id = typeof entry === 'string' ? entry : isObject(entry) ? entry.id : undefined;
    if (typeof id !== 'string') {
      errors.push(`${path}: ${value} names no position`);
      return value;
    }
    return id;
  }
  if (Array.isArray(value))
    return value.map((v, i) => resolve(v, root, `${path}[${i + 1}]`, errors));
  if (isObject(value)) {
    const out = {};
    for (const [k, v] of Object.entries(value))
      out[k] = resolve(v, root, path ? `${path}.${k}` : k, errors);
    return out;
  }
  return value;
}

/** The economy and a theme as one raw content object. Throws with every problem listed. */
export function mergeTheme(economy, theme, themeId = '?') {
  const errors = [];
  const merged = overlay(economy, theme, '', errors);
  const out = resolve(merged, merged, '', errors);
  if (errors.length > 0)
    throw new Error(`theme ${themeId} does not fit the economy:\n${errors.join('\n')}`);
  return out;
}
